/**
 * 공통 포맷팅 유틸리티 함수
 * 프로그램 전체에서 사용 가능한 포맷팅 함수들
 */

/**
 * 날짜를 YYYY-MM-DD 형식으로 포맷팅
 * @param {string|Date} dateString - 포맷팅할 날짜
 * @returns {string} 포맷팅된 날짜 문자열 또는 '-'
 */
export const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * 날짜를 YYYY-MM-DD HH:mm 형식으로 포맷팅 (상세 화면 처리일자용)
 * @param {string|Date} dateString - 포맷팅할 날짜
 * @returns {string} 포맷팅된 날짜시간 문자열 또는 '-'
 */
export const formatDateTime = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '-';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}`;
};

/**
 * 금액을 한국 원화 형식으로 포맷팅
 * @param {number|string} amount - 포맷팅할 금액
 * @returns {string} 포맷팅된 금액 문자열 또는 '-'
 */
export const formatCurrency = (amount) => {
  if (amount == null || amount === '') return '-';
  const num = Number(amount);
  if (Number.isNaN(num)) return '-';
  return new Intl.NumberFormat('ko-KR').format(num) + '원';
};

/**
 * 금액 표시 — null/미입력은 공란, 0은 "0원"
 * @param {number|string|null|undefined} amount
 * @returns {string}
 */
export const formatOptionalCurrency = (amount) => {
  if (amount == null || amount === '') return '';
  const num = Number(amount);
  if (Number.isNaN(num)) return '';
  return new Intl.NumberFormat('ko-KR').format(num) + '원';
};

/**
 * 파일 크기를 읽기 쉬운 형식으로 포맷팅
 * @param {number} bytes - 파일 크기 (바이트)
 * @returns {string} 포맷팅된 파일 크기 문자열 또는 '-'
 */
export const formatFileSize = (bytes) => {
  if (!bytes) return '-';
  if (bytes < 1024) {
    return bytes + ' B';
  } else if (bytes < 1024 * 1024) {
    return (bytes / 1024).toFixed(2) + ' KB';
  } else {
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  }
};

/**
 * 숫자를 천단위 콤마가 포함된 문자열로 포맷팅 (입력 필드용)
 * @param {number|string} value - 포맷팅할 숫자 또는 숫자 문자열
 * @returns {string} 천단위 콤마가 포함된 문자열 또는 빈 문자열 (미입력·NaN·음수)
 */
export const formatNumberWithCommas = (value) => {
  if (value === '' || value == null) return '';
  const numValue = typeof value === 'string'
    ? Number(value.replace(/,/g, ''))
    : Number(value);
  if (Number.isNaN(numValue) || numValue < 0) return '';
  return new Intl.NumberFormat('ko-KR').format(numValue);
};

/**
 * 입력값에서 숫자만 추출 (천단위 콤마 제거)
 * @param {string} value - 입력값
 * @returns {string} 숫자만 포함된 문자열
 */
export const extractNumbers = (value) => {
  if (!value) return '';
  return value.replace(/[^0-9]/g, '');
};

/**
 * 전화번호 표시: 숫자만 추출 후 하이픈 포맷.
 * 010 11자리는 010-XXXX-XXXX, 그 외 11자리는 XXX-XXXX-XXXX, 10자리는 02면 02-XXXX-XXXX 아니면 XXX-XXX-XXXX.
 * @param {string|null|undefined} phone
 * @returns {string}
 */
export const formatPhoneForDisplay = (phone) => {
  if (phone == null || phone === '') return '';
  const digits = String(phone).replace(/\D/g, '');
  if (digits.length === 11) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    if (digits.startsWith('02')) {
      return `${digits.slice(0, 2)}-${digits.slice(2, 6)}-${digits.slice(6)}`;
    }
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return digits;
};

/**
 * 전화번호 표시: 숫자만 추출 후 4번째(1-based) 자리부터 연속 4자리를 *로 마스킹 (부족하면 가능한 구간만)
 * @param {string|null|undefined} phone
 * @returns {string}
 */
export const formatPhoneMaskDigits4To7 = (phone) => {
  if (phone == null || phone === '') return '-';
  const str = String(phone).trim();
  if (str.includes('*')) return str;
  const digits = str.replace(/\D/g, '');
  if (digits.length === 0) return '-';
  if (digits.length < 4) return '-';
  const start = 3;
  const maskLen = 4;
  const maskEnd = Math.min(start + maskLen, digits.length);
  return `${digits.slice(0, start)}${'*'.repeat(maskEnd - start)}${digits.slice(maskEnd)}`;
};

/**
 * 이름 마스킹 (백엔드 ContactMaskingUtil.maskName 규칙과 동일)
 * 두 번째 글자만 * 로 마스킹 (2글자: 김*, 3글자: 김*수, 4글자 이상: 김*철수...)
 * 이미 * 가 포함된 값은 그대로 반환 (이중 마스킹 방지)
 * @param {string} name - 이름 문자열
 * @returns {string} 마스킹된 이름 또는 빈 문자열
 */
export const maskName = (name) => {
  if (name == null || typeof name !== 'string') return '';
  const trimmed = name.trim();
  if (trimmed.length === 0) return '';
  if (trimmed.includes('*')) return trimmed;
  if (trimmed.length === 1) return '*';
  return trimmed.charAt(0) + '*' + trimmed.substring(2);
};

/**
 * 생년월일 마스킹 (백엔드 ContactMaskingUtil.maskDateOfBirth 규칙과 동일)
 * YYYY-MM-DD / YYYY.MM.DD 등 → **.MM.DD (년 숨김). 이미 **.MM.DD 형식이면 그대로 반환.
 * @param {string} dateOfBirth - 생년월일 문자열
 * @returns {string} 마스킹된 생년월일 또는 빈 문자열
 */
export const maskDateOfBirth = (dateOfBirth) => {
  if (dateOfBirth == null || typeof dateOfBirth !== 'string') return '';
  const s = dateOfBirth.trim();
  if (s.length === 0) return '';
  if (s.endsWith('.**')) return s;
  const digits = s.replace(/\D/g, '');
  if (digits.length >= 8) {
    const month = digits.substring(4, 6);
    const day = digits.substring(6, 8);
    return '**.' + month + '.' + day;
  }
  if (digits.length >= 6) {
    const month = digits.substring(2, 4);
    const day = digits.substring(4, 6);
    return '**.' + month + '.' + day;
  }
  return '**.**.**';
};

const KOREA_ADMIN_REGION = /^(서울특별시|부산광역시|대구광역시|인천광역시|광주광역시|대전광역시|울산광역시|세종특별자치시|경기도|강원도|충청북도|충청남도|전라북도|전라남도|경상북도|경상남도|제주특별자치도)(.*)$/;
const ADDRESS_DETAIL_SUFFIX = ' ***';

/**
 * 주소 마스킹 (백엔드 ContactMaskingUtil.maskAddress 규칙과 동일)
 * 시/도+구(또는 군)까지 표시, 상세주소는 ***. 이미 마스킹된 값(*** 포함)이면 그대로 반환.
 * @param {string} address - 주소 문자열
 * @returns {string} 마스킹된 주소 또는 빈 문자열
 */
export const maskAddress = (address) => {
  if (address == null || typeof address !== 'string') return '';
  const s = address.trim();
  if (s.length === 0) return '';
  if (s.includes(' ***')) return s;
  const matcher = s.match(KOREA_ADMIN_REGION);
  if (matcher) {
    const sido = matcher[1];
    const rest = matcher[2].trim();
    const guIdx = rest.indexOf('구');
    const gunIdx = rest.indexOf('군');
    let end = -1;
    if (guIdx >= 0) end = guIdx + 1;
    if (gunIdx >= 0 && (end === -1 || gunIdx + 1 < end)) end = gunIdx + 1;
    if (end > 0) {
      const visiblePart = (sido + ' ' + rest.substring(0, end)).trim();
      const remaining = s.length > visiblePart.length ? s.substring(visiblePart.length).trim() : '';
      return remaining.length === 0 ? visiblePart : visiblePart + ADDRESS_DETAIL_SUFFIX;
    }
    if (s.length > 8) return s.substring(0, 8) + ADDRESS_DETAIL_SUFFIX;
    return s;
  }
  if (s.length > 8) return s.substring(0, 8) + ADDRESS_DETAIL_SUFFIX;
  return s;
};

/**
 * 연락처관리 목록용 주소 표시: 시/도+구(군)까지 또는 앞 10자만 보이고 나머지는 ... 처리
 * @param {string} address - 주소 문자열
 * @returns {string} 목록 표시용 주소 또는 '-'
 */
export const formatAddressForContactList = (address) => {
  if (address == null || typeof address !== 'string') return '-';
  const s = address.trim();
  if (s.length === 0) return '-';

  const matcher = s.match(KOREA_ADMIN_REGION);
  if (matcher) {
    const sido = matcher[1];
    const rest = matcher[2].trim();
    const guIdx = rest.indexOf('구');
    const gunIdx = rest.indexOf('군');
    let end = -1;
    if (guIdx >= 0) end = guIdx + 1;
    if (gunIdx >= 0 && (end === -1 || gunIdx + 1 < end)) end = gunIdx + 1;
    if (end > 0) {
      const visiblePart = (sido + ' ' + rest.substring(0, end)).trim();
      const remaining = s.length > visiblePart.length ? s.substring(visiblePart.length).trim() : '';
      return remaining.length === 0 ? visiblePart : `${visiblePart}...`;
    }
  }

  if (s.length <= 10) return s;
  return `${s.substring(0, 10)}...`;
};

/**
 * API/런타임 오류 메시지에서 Exception 접두어 제거
 * @param {string|null|undefined} message
 * @param {string} fallback
 * @returns {string}
 */
export const formatApiErrorMessage = (message, fallback = '') => {
  const raw = message || fallback || '';
  return String(raw)
    .replace(/Exception:\s*/gi, '')
    .replace(/^Exception\s*/gi, '')
    .trim();
};
